import { useEffect, useRef } from "react";
import { Home, RotateCcw, FastForward } from "lucide-react";
import { drawToast, drawHeart, drawHero } from "@/game/sprites";
import { sfx, playMusic, stopMusic } from "@/game/audio";
import { DIFFICULTIES } from "@/game/config";
import { PixelButton } from "./PixelButton";

const CW = 960, CH = 540, TX = 600, TY = 300;

const drawScene = (ctx, t) => {
  const k = t % 18;
  ctx.imageSmoothingEnabled = true;
  ctx.fillStyle = "#FDE68A"; ctx.fillRect(0, 0, CW, CH);
  ctx.fillStyle = "#FCD34D"; for (let i = 0; i < CW; i += 60) ctx.fillRect(i, 0, 30, 380);
  ctx.fillStyle = "#FBBF24"; ctx.beginPath(); ctx.arc(140, 110, 50, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#92400E"; ctx.fillRect(0, 380, CW, 160);
  ctx.fillStyle = "#78350F"; ctx.fillRect(0, 380, CW, 10);
  ctx.fillStyle = "#F8FAFC"; ctx.fillRect(TX - 110, 440, 220, 16);
  drawToast(ctx, TX, TY, 170);
  const spread = Math.min(1, Math.max(0, (k - 4) / 4));
  if (spread > 0) {
    ctx.fillStyle = "#FEF08A";
    ctx.fillRect(TX - 60, TY - 40, 120 * spread, 110);
    ctx.fillStyle = "#FFFBEB"; ctx.fillRect(TX - 50, TY - 30, 30 * spread, 8);
  }
  const bites = Math.max(0, Math.floor((k - 9) / 1.2));
  ctx.fillStyle = "#FDE68A";
  for (let i = 0; i < Math.min(bites, 5); i++) {
    ctx.beginPath(); ctx.arc(TX + 80 - i * 4, TY - 80 + i * 34, 26, 0, Math.PI * 2); ctx.fill();
  }
  let hx = 120 + Math.min(1, k / 3.5) * 290, hy = 400;
  if (k > 3.5) hx = 420;
  if (k > 8.5) { hx = 420 + Math.min(1, (k - 8.5) / 0.6) * 150; hy = 400 - Math.sin(Math.min(1, (k - 8.5) / 0.6) * Math.PI) * 80; }
  if (k > 9.1) { hx = 570; hy = 400 - Math.abs(Math.sin(k * 6)) * 12; }
  const happy = k > 4;
  ctx.save(); ctx.translate(hx, hy); ctx.scale(2.6, 2.6);
  drawHero(ctx, { x: 0, y: 0, dir: 1, walk: k < 3.5 ? k : 0, onGround: true }, k, happy);
  ctx.restore();
  if (k > 4 && k < 8.5) {
    const a = Math.sin(k * 10) * 0.5;
    ctx.save(); ctx.translate(hx + 40, hy - 10); ctx.rotate(-0.8 + a);
    ctx.fillStyle = "#94A3B8"; ctx.fillRect(0, -3, 60, 6);
    ctx.fillStyle = "#FEF08A"; ctx.fillRect(40, -6, 20, 4);
    ctx.restore();
  }
  ctx.font = '30px "Lilita One", sans-serif'; ctx.textAlign = "center";
  if (k > 9.1) {
    for (let i = 0; i < 6; i++) {
      const p = ((k * 0.6 + i / 6) % 1);
      ctx.globalAlpha = 1 - p;
      drawHeart(ctx, hx - 80 + i * 32, hy - 60 - p * 160, 26);
    }
    ctx.globalAlpha = 1;
    if (Math.sin(k * 6) > 0) { ctx.fillStyle = "#7C2D12"; ctx.fillText("NOM NOM", hx, hy - 70); }
  } else if (k < 3.5) {
    ctx.fillStyle = "#7C2D12"; ctx.fillText("FINALLY... TOAST!", 300, 300);
  }
};

export const EndingScene = ({ stats, playerName, onHome, onReplay, onContinue }) => {
  const ref = useRef(null);
  useEffect(() => {
    sfx("victory");
    const music = setTimeout(() => playMusic("ending"), 1600);
    const ctx = ref.current.getContext("2d");
    let raf, start = performance.now();
    const loop = (now) => { drawScene(ctx, (now - start) / 1000); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); clearTimeout(music); stopMusic(); };
  }, []);

  return (
    <div className="ending" data-testid="ending-scene-container">
      <canvas ref={ref} width={CW} height={CH} className="ending-canvas pixel-canvas" data-testid="ending-canvas" />
      <div className="credits-mask">
        <div className="credits-roll" data-testid="credits-roll-content">
          <h2>The End</h2>
          <p>Butter Boy finally got his toast.</p>
          <p>He spread himself thin, and it was delicious.</p>
          <h3>Hero</h3><p>{playerName} as Butter Boy</p>
          <h3>Run Stats</h3>
          <p>Score {stats.score.toLocaleString()}</p>
          <p>{stats.toast} toast collected</p>
          <p>{stats.kills} crumbs defeated</p>
          <p>{stats.livesLost} lives lost</p>
          <p>Mode: {DIFFICULTIES[stats.difficulty].label}</p>
          <h3>Villains</h3>
          <p>Crumb Goblins · Jam Slimes · Mold Blobs</p>
          <p>Honey Bees · Crunchy Beetles · Crumb Crabs</p>
          <p>Toaster Turrets · Burnt Ghosts</p>
          <p>Crumb King · Jam Kraken · Mold Titan · Burnt Baron</p>
          <p>and the Toaster Overlord</p>
          <h3>Weapons Dept.</h3>
          <p>Butter Knife · Dinner Fork · Baguette Sword · Golden Spatula</p>
          <p>Whisk Flail · Rolling Pin Maul · Chef's Cleaver · Butter Excalibur</p>
          <h3>Catering</h3><p>Every single slice of toast</p>
          <div data-testid="credits-creator-note">
            <h2>A Note From The Creator</h2>
            <p className="creator-note">Thank you for playing my game!</p>
            <p>I am new to making apps, and Toast &amp; Butter Quest is one of my very first adventures.</p>
            <p>Every jump, every slice of toast and every boss you beat means the world to me.</p>
            <p>I truly appreciate all of your support.</p>
            <p>Butter Boy's journey is not over yet...</p>
            <h2>See you soon in Part Two!</h2>
          </div>
        </div>
      </div>
      <div className="ending-actions">
        {onContinue && (
          <PixelButton onClick={onContinue} data-testid="ending-continue-button"><FastForward size={16} /> Continue: 15 Bonus Stages</PixelButton>
        )}
        <PixelButton onClick={onReplay} data-testid="ending-play-again-button"><RotateCcw size={16} /> Play Again</PixelButton>
        <PixelButton variant="ghost" onClick={onHome} data-testid="ending-return-home-button"><Home size={16} /> Title</PixelButton>
      </div>
    </div>
  );
};
